import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/CopyButton';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { pitchBullets, type ViewedListing } from '@/lib/dealBriefPitch';

export const FEATURE_META = {
  id: 'deal-brief',
  label: 'Deal brief',
  description: "Your team's private notes on a company plus which of your listings they looked at recently, with a short copyable pitch.",
  triggerType: 'panel' as const,
  personas: ['seller', 'buyer_seller', 'buyer'] as ('seller' | 'buyer_seller' | 'buyer')[],
};

interface Note { id: string; body: string; created_at: string; author_name: string; is_mine: boolean }
interface Brief { notes: Note[]; viewed: ViewedListing[]; days: number }

export default function DealBrief({ counterpartyCompanyId, counterpartyName }: { counterpartyCompanyId: string; counterpartyName?: string | null }) {
  useFeatureRegistration(FEATURE_META);
  const qc = useQueryClient();
  const key = ['counterparty-brief', counterpartyCompanyId];
  const [draft, setDraft] = useState('');

  const { data } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_counterparty_brief' as never, { _counterparty_company_id: counterpartyCompanyId } as never);
      if (error) throw error;
      return data as unknown as Brief;
    },
  });

  const add = useMutation({
    mutationFn: async (body: string) => {
      const { error } = await supabase.rpc('add_counterparty_note' as never, { _counterparty_company_id: counterpartyCompanyId, _body: body } as never);
      if (error) throw error;
    },
    onSuccess: () => { setDraft(''); qc.invalidateQueries({ queryKey: key }); },
    onError: () => toast.error("Couldn't save that note", { description: 'Please try again.' }),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_counterparty_note' as never, { _note_id: id } as never);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
    onError: () => toast.error("Couldn't remove that note"),
  });

  const pitch = pitchBullets(data?.viewed ?? [], counterpartyName);
  const submit = () => { const b = draft.trim(); if (b && !add.isPending) add.mutate(b); };

  return (
    <div className="space-y-2.5 border-t border-border pt-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">Team notes</span>
        <span className="text-[11px] text-muted-foreground">Only your team sees these</span>
      </div>

      {pitch.length > 0 && (
        <div className="rounded-md bg-muted/50 p-2 text-xs">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-0.5">{pitch.map((l) => <div key={l}>{l}</div>)}</div>
            <CopyButton value={pitch.join('\n')} />
          </div>
        </div>
      )}

      {(data?.notes ?? []).slice(0, 4).map((n) => (
        <div key={n.id} className="group flex items-start gap-2 text-xs">
          <div className="min-w-0 flex-1">
            <div className="whitespace-pre-wrap break-words">{n.body}</div>
            <div className="text-[11px] text-muted-foreground">
              {n.author_name} · {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
            </div>
          </div>
          {n.is_mine && (
            <button type="button" aria-label="Remove note" className="text-muted-foreground opacity-60 hover:opacity-100" onClick={() => remove.mutate(n.id)}>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}

      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, 2000))}
        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
        placeholder="Add a note — terms, preferences, what they asked for"
        className="min-h-[56px] text-xs"
      />
      {draft.trim() && (
        <div className="flex justify-end">
          <Button size="sm" onClick={submit} disabled={add.isPending}>Save note</Button>
        </div>
      )}
    </div>
  );
}
